import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
  getDataSourceNumericId,
  listDataSources,
} from '../DataSources/datasourcesApi';
import { startProfileJob } from './profileApi';
import { ensureConnectionTested } from 'Utils/connectionGate';

/**
 * Single-data-source picker for kicking off a profile (schema/file metadata) run.
 * Both relational and file-folder sources are eligible now — the metadata-service
 * profile worker accepts the `file` modality and returns a whole-folder inventory
 * (total size + per-file listing) for file sources.
 */
const RunProfileModal = ({ show, onHide, onSuccess }) => {
  const history = useHistory();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setSelectedId('');
    setSubmitError(null);
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listDataSources()
      .then((rows) => {
        if (cancelled) return;
        const eligible = (rows || []).filter(
          (r) => getDataSourceNumericId(r) != null
        );
        setSources(eligible);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e?.message || 'Failed to load data sources');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const sortedSources = useMemo(
    () => [...sources].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [sources]
  );

  const handleRun = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Verify (or establish) a successful connection test first; on failure the
      // helper routes the user to the source editor to fix the connection.
      const gate = await ensureConnectionTested(Number(selectedId), { history });
      if (!gate.ok) {
        setSubmitError(`Connection test failed: ${gate.message || 'the data source is unreachable.'}`);
        onHide();
        return;
      }
      const job = await startProfileJob(Number(selectedId));
      if (onSuccess) onSuccess(job);
      onHide();
    } catch (e) {
      setSubmitError(e?.message || 'Failed to start profile run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Run profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='d-flex align-items-center'>
            <Spinner animation='border' size='sm' className='mr-2' />
            <span>Loading data sources…</span>
          </div>
        ) : loadError ? (
          <Alert variant='danger'>{loadError}</Alert>
        ) : sortedSources.length === 0 ? (
          <div>No data sources available to profile.</div>
        ) : (
          <Form.Group>
            <Form.Label>Data source</Form.Label>
            <Form.Control
              as='select'
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value=''>Select a data source…</option>
              {sortedSources.map((s) => {
                const id = getDataSourceNumericId(s);
                const engine = s.type || s.engine;
                return (
                  <option key={id} value={id}>
                    {s.name}{engine ? ` (${engine})` : ''}
                  </option>
                );
              })}
            </Form.Control>
          </Form.Group>
        )}
        {submitError && (
          <Alert variant='danger' className='mt-3'>{submitError}</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleRun}
          disabled={!selectedId || submitting || loading}
        >
          {submitting ? (
            <>
              <Spinner animation='border' size='sm' className='mr-2' />
              Starting…
            </>
          ) : (
            'Run'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RunProfileModal;
